<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="fetchDetail" />
        <div v-else>
            <b-card>
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                    <h5 class="mb-0">
                        <i class="fa fa-fw fa-search mr-2" />Detail Temuan Inventaris
                    </h5>
                    <div>
                        <b-button v-if="canPropose" variant="primary" @click="$bvModal.show('m-data-perubahan-temuan-inventory')">
                            <i class="fa fa-pencil mr-2" />{{ isRejected ? "Revisi Data Perubahan" : "Isi Data Perubahan" }}
                        </b-button>
                        <b-button v-if="canAudit" variant="primary" @click="$bvModal.show('m-audit-temuan-inventory')">
                            <i class="fa fa-check mr-2" />Review Audit
                        </b-button>
                        <span v-if="canComplete" v-b-tooltip.hover :title="completeBlockedReason" class="d-inline-block">
                            <b-button
                                variant="success"
                                :disabled="!!completeBlockedReason"
                                @click="$bvModal.show('m-complete-temuan-inventory')"
                            >
                                <i class="fa fa-check-square-o mr-2" />Approve ke Inventory
                            </b-button>
                        </span>
                    </div>
                </div>
                <hr />
                <b-alert :show="inventory.status === INVENTORY_STATUS.REQUEST_BUYBACK_PGI" variant="warning">
                    <i class="fa fa-exclamation-triangle mr-2" />Barang sedang direquest kirim balik ke PGI.
                </b-alert>
                <b-alert :show="variance.status === VARIANCE_STATUS.CANCELLED" variant="danger">
                    <strong>Temuan dibatalkan</strong> pada {{ formatDateTime(variance.cancelled_at) }}.
                    Alasan: {{ variance.cancel_reason || "-" }}
                </b-alert>
                <b-alert v-if="isRejected && lastReject" show variant="danger">
                    <strong>Ditolak audit</strong> oleh {{ lastReject.reviewed_by || "-" }} pada {{ formatDateTime(lastReject.reviewed_at) }}.
                    Alasan: {{ lastReject.notes || "-" }}
                </b-alert>
                <b-row>
                    <b-col md="6">
                        <table class="table table-borderless table-sm mb-0">
                            <tr>
                                <td width="160">No. Faktur</td>
                                <td>: <strong>{{ variance.no_faktur || "-" }}</strong></td>
                            </tr>
                            <tr>
                                <td>Status</td>
                                <td>
                                    :
                                    <b-badge :variant="statusBadge.variant">{{ statusBadge.text }}</b-badge>
                                </td>
                            </tr>
                            <tr>
                                <td>Sumber</td>
                                <td>: {{ originLabels[variance.origin_type] || "-" }}</td>
                            </tr>
                            <tr>
                                <td>Kategori</td>
                                <td>: {{ variance.category || "-" }}</td>
                            </tr>
                            <tr v-if="detail.inventory">
                                <td>Status Barang</td>
                                <td>: {{ inventory.status_name }}</td>
                            </tr>
                        </table>
                    </b-col>
                    <b-col md="6">
                        <table class="table table-borderless table-sm mb-0">
                            <template v-if="detail.inventory">
                                <tr>
                                    <td width="160">Cabang</td>
                                    <td>: {{ inventory.branch || "-" }}</td>
                                </tr>
                                <tr>
                                    <td>Office</td>
                                    <td>: {{ inventory.office || "-" }}</td>
                                </tr>
                                <tr>
                                    <td>Tanggal Kirim</td>
                                    <td>: {{ $helper.parseDate(inventory.delivery_date) }}</td>
                                </tr>
                            </template>
                            <template v-if="detail.consignment">
                                <tr>
                                    <td width="160">Kode Titip Jual</td>
                                    <td>: <strong>{{ detail.consignment.code }}</strong></td>
                                </tr>
                                <tr>
                                    <td>Batch Titip Jual</td>
                                    <td>
                                        :
                                        <router-link :to="'/barang-masuk/titip-jual/' + detail.consignment.batch_id">
                                            {{ detail.consignment.batch_code }}
                                        </router-link>
                                    </td>
                                </tr>
                            </template>
                            <tr>
                                <td>Dibuat</td>
                                <td>: {{ variance.created_by || "-" }} - {{ formatDateTime(variance.created_at) }}</td>
                            </tr>
                            <tr v-if="variance.final_at">
                                <td>Diaudit</td>
                                <td>: {{ variance.final_by || "-" }} - {{ formatDateTime(variance.final_at) }}</td>
                            </tr>
                            <tr v-if="variance.completed_at">
                                <td>Selesai</td>
                                <td>: {{ variance.completed_by || "-" }} - {{ formatDateTime(variance.completed_at) }}</td>
                            </tr>
                        </table>
                    </b-col>
                </b-row>
            </b-card>

            <b-row>
                <b-col lg="7">
                    <b-card header="Perbandingan Data">
                        <table class="table table-sm table-bordered mb-2">
                            <thead class="thead-light">
                                <tr>
                                    <th width="150">Detail</th>
                                    <th>{{ originColumnTitle }}</th>
                                    <th>Data Perubahan SM</th>
                                    <th>Final Auditor</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="field in fields" :key="field.key">
                                    <td class="font-weight-bold">{{ field.label }}</td>
                                    <td>{{ displayField(detail.origin_data, field) }}</td>
                                    <td :class="{ 'table-warning': isFieldDiff(detail.origin_data, detail.sm_data, field, variance.origin_type) }">
                                        {{ displayField(detail.sm_data, field) }}
                                    </td>
                                    <td :class="{ 'table-warning': isFieldDiff(detail.origin_data, detail.final_data, field, variance.origin_type) }">
                                        {{ displayField(detail.final_data, field) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <small class="text-muted">
                            <span class="legend table-warning" /> berbeda dari {{ originColumnTitle }}
                        </small>
                    </b-card>

                    <b-card v-if="reviews.length" header="Riwayat Review Audit">
                        <table class="table table-sm table-bordered mb-0">
                            <thead class="thead-light">
                                <tr>
                                    <th>Waktu</th>
                                    <th>Oleh</th>
                                    <th>Aksi</th>
                                    <th>Catatan</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="review in reviews" :key="review.id">
                                    <td>{{ formatDateTime(review.reviewed_at) }}</td>
                                    <td>{{ review.reviewed_by || "-" }}</td>
                                    <td>
                                        <b-badge :variant="review.action === 'approve' ? 'success' : 'danger'">
                                            {{ review.action === "approve" ? "Disetujui" : "Ditolak" }}
                                        </b-badge>
                                    </td>
                                    <td class="notes">{{ review.notes || "-" }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </b-card>

                    <b-card header="Catatan & Media">
                        <div class="mb-3">
                            <strong>Catatan SM</strong>
                            <div class="notes">{{ variance.sm_notes || "-" }}</div>
                        </div>
                        <div class="mb-3">
                            <strong>Catatan Final Auditor</strong>
                            <div class="notes">{{ variance.final_notes || "-" }}</div>
                        </div>
                        <template v-if="media.physical.length">
                            <strong>Foto Fisik</strong>
                            <div class="d-flex flex-wrap mt-2 mb-3">
                                <a v-for="(m, index) in media.physical" :key="'p' + index" :href="m.url" target="_blank" @click="previewMedia($event, m.url)" class="mr-2 mb-2 text-center">
                                    <MediaImage :src="m.url" class="media-thumbnail" :alt="m.category" />
                                    <small class="d-block">{{ m.category }}</small>
                                </a>
                            </div>
                        </template>
                        <strong>Foto & Video Temuan</strong>
                        <div v-if="media.evidence.length" class="d-flex flex-wrap mt-2">
                            <a
                                v-for="(m, index) in media.evidence"
                                :key="index"
                                :href="m.url"
                                target="_blank"
                                @click="previewMedia($event, m.url)"
                                class="mr-2 mb-2 position-relative"
                            >
                                <template v-if="m.type === 'video'">
                                    <video :src="m.url" class="media-thumbnail" preload="metadata"></video>
                                    <span class="play-icon"><i class="fa fa-play" /></span>
                                </template>
                                <MediaImage v-else :src="m.url" class="media-thumbnail" alt="Media temuan" />
                            </a>
                        </div>
                        <div v-else class="text-muted">-</div>
                    </b-card>
                </b-col>

                <b-col lg="5">
                    <template v-if="canViewChat">
                        <ForumTemuan
                            v-if="variance.status !== VARIANCE_STATUS.PENDING_SM"
                            :variance-id="variance.id"
                            :no-faktur="variance.no_faktur"
                            :status="variance.status"
                            base-url="inventory-variances"
                            upload-folder="inventory-variance-chats"
                            :chat-permission="chatPermission"
                            @message-sent="fetchDetail"
                        />
                        <b-alert v-else show variant="secondary">
                            <i class="fa fa-lock mr-2" />Forum diskusi tersedia setelah SM mengisi data perubahan.
                        </b-alert>
                    </template>
                </b-col>
            </b-row>

            <ModalDataPerubahan
                context="proposal"
                :variance-id="variance.id"
                :no-faktur="variance.no_faktur"
                :defaults="detail.sm_data || detail.origin_data"
                :default-category="variance.category"
                :default-media="detail.sm_media"
                :title="isRejected ? 'Revisi Data Perubahan Temuan' : ''"
                @saved="fetchDetail"
            />
            <ModalAuditTemuan :detail="detail" @saved="fetchDetail" />
            <ModalCompleteTemuan :detail="detail" @completed="fetchDetail" />
        </div>
    </div>
</template>

<script>
import EnumPermission from "@/shared/enum-permissions";
import {
    VARIANCE_STATUS,
    INVENTORY_STATUS,
    ORIGIN_LABELS,
    COMPARE_FIELDS,
    getVarianceStatusBadge,
    displayField,
    isFieldDiff,
    splitMedia
} from "@/shared/inventory-variance";
import ForumTemuan from "../temuan-emas/ForumTemuan.vue";
import ModalDataPerubahan from "./ModalDataPerubahan.vue";
import ModalAuditTemuan from "./ModalAuditTemuan.vue";
import ModalCompleteTemuan from "./ModalCompleteTemuan.vue";
import MediaImage from "./MediaImage.vue";
import { previewMedia } from "@/shared/heic";

export default {
    components: {
        ForumTemuan,
        ModalDataPerubahan,
        ModalAuditTemuan,
        ModalCompleteTemuan,
        MediaImage
    },
    data() {
        return {
            initStatus: 0,
            detail: null,
            originLabels: ORIGIN_LABELS,
            fields: COMPARE_FIELDS,
            chatPermission: EnumPermission.INVENTORY_VARIANCE_CHAT,
            VARIANCE_STATUS,
            INVENTORY_STATUS
        };
    },
    computed: {
        variance() {
            return this.detail ? this.detail.variance : {};
        },
        inventory() {
            return (this.detail && this.detail.inventory) || {};
        },
        isConsignment() {
            return this.variance.origin_type === "CONSIGNMENT";
        },
        media() {
            return splitMedia(this.detail.sm_media);
        },
        statusBadge() {
            return getVarianceStatusBadge(this.variance.status);
        },
        originColumnTitle() {
            return { RESET: "Data Sebelumnya", CONSIGNMENT: "Data GA" }[this.variance.origin_type] || "Data PGI";
        },
        isRejected() {
            return this.variance.status === VARIANCE_STATUS.REJECTED_AUDIT;
        },
        reviews() {
            return (this.detail && this.detail.reviews) || [];
        },
        lastReject() {
            return this.reviews.filter(r => r.action === "reject").pop();
        },
        canPropose() {
            return (
                [VARIANCE_STATUS.PENDING_SM, VARIANCE_STATUS.REJECTED_AUDIT].includes(this.variance.status) &&
                (this.isAllowed(EnumPermission.INVENTORY_VARIANCE) ||
                    (this.isConsignment && this.isAllowed(EnumPermission.APPROVE_CONSIGNMENT)))
            );
        },
        canAudit() {
            return (
                [VARIANCE_STATUS.PENDING_AUDIT, VARIANCE_STATUS.IN_DISCUSSION].includes(this.variance.status) &&
                this.isAllowed(EnumPermission.INVENTORY_VARIANCE_AUDITOR)
            );
        },
        canComplete() {
            const permission = this.isConsignment ? EnumPermission.APPROVE_CONSIGNMENT : EnumPermission.APPROVE_BARANG;
            return this.variance.status === VARIANCE_STATUS.APPROVED_AUDIT && this.isAllowed(permission);
        },
        completeBlockedReason() {
            if (this.detail.can_complete) return "";
            if (this.isConsignment) return "Item titip jual sudah tidak berstatus PENDING";
            return `Barang berstatus ${this.inventory.status_name}. Hanya barang PENDING yang bisa di-approve.`;
        },
        canViewChat() {
            return this.isAllowed(EnumPermission.INVENTORY_VARIANCE_CHAT);
        }
    },
    methods: {
        previewMedia,
        displayField,
        isFieldDiff,

        isAllowed(permission) {
            return this.$helper.isAllowed(permission.toString());
        },

        formatDateTime(date) {
            return date ? `${this.$helper.parseDate(date)} ${this.$helper.parseHourMinute(date)}` : "-";
        },

        // Refreshes keep the page mounted so open modals are not torn down.
        fetchDetail() {
            if (!this.detail) this.initStatus = 0;
            return this.$axios
                .get(`inventory-variances/${this.$route.params.id}`)
                .then(res => {
                    this.detail = res.data.data;
                    this.initStatus = 1;
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                    if (!this.detail) this.initStatus = -1;
                });
        }
    },
    created() {
        this.fetchDetail();
    }
};
</script>

<style scoped>
.media-thumbnail {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.notes {
    white-space: pre-wrap;
}

.legend {
    display: inline-block;
    width: 12px;
    height: 12px;
    vertical-align: middle;
    border: 1px solid #dee2e6;
}
</style>
